/* global currentUser:true */
import React from 'react'
import PropTypes from 'prop-types'
import { isEmpty, isString } from 'lodash'
import classNames from 'classnames'

import fetch from 'lib/fetch'
import * as blUrls from '../lib/urls'
import { track, tracking, dummyTracking } from 'lib/analytics'

import ReCAPTCHA from 'shared/recaptcha'
import { PrimaryButton } from 'components/buttons'
import { Input, SecretInput } from 'components/forms'
import { DangerAlert } from 'components/alerts'

const defaultRedirect = () => {
  window.location.href = window.currentUser.registryUrlSlug
    ? blUrls.registryHomePath
    : blUrls.newRegistryPath
}

export const redirect = (url) => {
  if (url) {
    if (isString(url)) {
      window.location.href = url
    } else {
      defaultRedirect()
      throw new Error(`Cannot redirect to non-string url: ${url}`)
    }
  } else {
    defaultRedirect()
  }
}

const recaptchaRef = React.createRef()

const DefaultFallback = (
  <p className="text-right">
    Don&apos;t have a Babylist account?{' '}
    <a href={blUrls.newRegistryPath}>Create one now</a>
  </p>
)

@tracking()
export class LoginFormContainer extends React.Component {
  static propTypes = {
    onAuthSuccess: PropTypes.func,
    redirectUrl: PropTypes.string,
    recaptcha: PropTypes.bool,
    fallback: PropTypes.element, // fallback action if no account (default signup)
  }

  static defaultProps = {
    onAuthSuccess: ({ redirectUrl }) => {
      redirect(redirectUrl)
    },
    recaptcha: false,
    tracking: dummyTracking,
    fallback: DefaultFallback,
  }

  state = {
    login: null,
    password: null,
    errors: {},
    recaptchaCode: null,
  }

  recaptchaRef = React.createRef()

  handleChange = (event, key) => {
    let state = { ...this.state }
    state[key] = event.target.value
    this.setState(state)
  }

  handleRecaptchaChange = (recaptchaCode) => {
    this.setState({ recaptchaCode })
  }

  // @tracking -> not using the decorator as we have inconsistent return
  loginRequest = (event) => {
    const tracker = this.props.tracking
    event.preventDefault()

    // exit early if we're already in a loading state.
    // user could have hit Enter instead of clicking button.
    if (this.state.loading) {
      return
    }

    this.setState({ loading: true })

    if (!this.state.login || !this.state.password) {
      this.setState({
        loading: false,
        errors: { emptyForm: 'You must enter an email address and password' },
      })
      return
    }

    if (this.props.recaptcha && !this.state.recaptchaCode) {
      this.setState({
        loading: false,
        errors: {
          recaptcha: 'You must verify with reCAPTCHA before logging in.',
        },
      })
      return
    }

    // clear out any errors that existed from a previous submission
    if (!isEmpty(this.state.errors)) {
      this.setState({ errors: {} })
    }

    return fetch(blUrls.apiV3UserSessionPath, {
      method: 'POST',
      body: JSON.stringify({
        user_session: {
          login: this.state.login,
          password: this.state.password,
        },
        authenticity: {
          token: this.state.recaptchaCode,
          version: 'web',
          type: 'recaptcha',
        },
        redirect_url: this.props.redirectUrl,
      }),
    })
      .then((resp) => {
        window.currentUser = resp.user

        tracker.trackEvent({ event: track.logInCompleted, user: resp.user })

        // fire an event that can be used to fetch any additional resources needed
        // after the user is correctly authenticated
        PubSub.publish('auth.success')

        // ⚠️Call last incase callback has a side effect like redirecting to another page.
        this.props.onAuthSuccess(resp)
        return resp
      })
      .catch((resp) => {
        if (this.props.recaptcha) {
          recaptchaRef.current.reset()
        }
        let errors = resp.error
        this.setState({ errors, loading: false })
      })
  }

  render() {
    return (
      <LoginForm
        {...this.state}
        fallback={this.props.fallback}
        recaptcha={this.props.recaptcha}
        onChange={this.handleChange}
        onRecaptchaChange={this.handleRecaptchaChange}
        loginRequest={this.loginRequest}
      />
    )
  }
}

export class LoginForm extends React.Component {
  static propTypes = {
    onChange: PropTypes.func,
    loginRequest: PropTypes.func,
    errors: PropTypes.object,
    login: PropTypes.string,
    password: PropTypes.string,
    onRecaptchaChange: PropTypes.func,
    recaptcha: PropTypes.bool,
    loading: PropTypes.bool,
    fallback: PropTypes.element, // fallback action if no account (default signup)
  }

  static defaultProps = {
    fallback: DefaultFallback,
  }

  handleChange = (key, event) => {
    this.props.onChange(event, key)
  }

  render() {
    const { fallback } = this.props

    return (
      <React.Fragment>
        {this.props.errors.emptyForm ? (
          <DangerAlert>
            <div className="text-center">{this.props.errors.emptyForm}</div>
          </DangerAlert>
        ) : null}
        <form method="post" onSubmit={this.props.loginRequest}>
          <Input
            autoComplete="username"
            ref={this.setEmailRef}
            type="email"
            name="email"
            label="Email Address"
            autoFocus={true}
            value={this.props.login}
            onChange={this.handleChange.bind(this, 'login')}
            onBlur={this.handleChange.bind(this, 'login')}
          />

          <SecretInput
            autoComplete="current-password"
            name="password"
            label="Password"
            value={this.props.password}
            error={this.props.errors.message ? this.props.errors.message : null}
            onChange={this.handleChange.bind(this, 'password')}
            onBlur={this.handleChange.bind(this, 'password')}
          />

          {this.props.recaptcha && (
            <div
              className={classNames(
                { 'has-error': this.props.errors.recaptcha },
                'mbxl'
              )}
            >
              <ReCAPTCHA
                onChange={this.props.onRecaptchaChange}
                ref={recaptchaRef}
              />
              {this.props.errors.recaptcha && (
                <p className="h7 help-block">{this.props.errors.recaptcha}</p>
              )}
            </div>
          )}

          <div className="btn-group btn-group--stretch">
            <PrimaryButton type="submit" disabled={this.props.loading}>
              Log In
            </PrimaryButton>
          </div>
        </form>
        <div className="mtl h6">
          <p className="text-right">
            Forgot your password?{' '}
            <a href="/forgot_password">Request a new one</a>
          </p>
          {fallback}
        </div>
      </React.Fragment>
    )
  }
}
